import React, { useEffect } from 'react';
import { AlertTriangle, ArrowRight, EyeOff } from 'lucide-react';
import { TopBar } from '../../components/layout/TopBar';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { UNASSIGNED_PRODUCTS } from '../../data/unassignedProducts';
// Recommended tree is rendered from the shared source of truth so this report
// can never drift from the customer-facing navigation.
import { PRODUCT_NAV } from '../../data/categoryNav';
export function CategoryCleanupReview() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Review / Unassigned — Internal Category Cleanup';
    return () => {
      document.title = previousTitle;
    };
  }, []);
  const deactivated = UNASSIGNED_PRODUCTS.filter(p => p.action === 'Approved to deactivate');
  const assigned = UNASSIGNED_PRODUCTS.filter(p => p.action === 'Approved assignment');
  const STATS = [{
    value: '1,744',
    label: 'Active products'
  }, {
    value: '7',
    label: 'Strict active root-only products remaining'
  }, {
    value: '15',
    label: 'Root-only cleanup decisions logged'
  }];
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-2058" data-mp="3k644kjljilit">
      <TopBar data-id="element-2059" data-mp="3o7xzeb920vb2" />
      <Header data-id="element-2060" data-mp="3o7fwwwvstkup" />

      <main className="flex-grow py-10 lg:py-14" data-id="element-2061" data-mp="3o6wd1aml8me1">
        <div className="max-w-5xl mx-auto px-4" data-id="element-2062" data-mp="3ahcgwnniindp">
          <div className="inline-flex items-center gap-2 bg-brand-charcoal text-white text-xs font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-sm mb-5" data-id="element-2063" data-mp="3afhut60i8p81">
            <EyeOff size={14} aria-hidden="true" data-id="element-2064" data-mp="30jer3vq08iiz" />
            Internal report — not linked in site navigation
          </div>

          <h1 className="text-3xl lg:text-4xl font-serif font-bold text-brand-charcoal mb-4" data-id="element-2065" data-mp="35irgoo89g3p9">
            Review / Unassigned products
          </h1>
          <p className="text-lg text-brand-muted leading-relaxed max-w-3xl" data-id="element-2066" data-mp="2whbdb0fv51uj">
            These are products assigned only to root / All Categories, or with
            no meaningful category assignment. This is a strict root-only view —
            items assigned to root <em data-id="element-2067" data-mp="x8t8ykcz7eog">plus</em> another real category are
            excluded. Review / Unassigned is an internal, report-only bucket: it
            is not a customer-facing category and does not appear in the main
            navigation, the Shop by Need cards, or any sample product grid.
          </p>

          <dl className="mt-6 grid gap-4 sm:grid-cols-3 max-w-3xl" data-id="element-2068" data-mp="1kzfartwe02q0">
            {STATS.map(stat => <div key={stat.label} className="bg-white border border-brand-border rounded-xl p-5" data-id="element-2069" data-mp="1g1neyosmfwgj">
                <dt className="text-sm text-brand-muted leading-snug" data-id="element-2070" data-mp="170pcwc1qtz0l">
                  {stat.label}
                </dt>
                <dd className="text-3xl font-serif font-bold text-brand-charcoal mt-2" data-id="element-2071" data-mp="l58rhcve6t6">
                  {stat.value}
                </dd>
              </div>)}
          </dl>

          <div className="mt-6 bg-brand-goldSoft border border-brand-gold/40 rounded-xl p-5 flex items-start gap-3 max-w-3xl" data-id="element-2072" data-mp="2795l75dm8pbf">
            <AlertTriangle size={20} className="text-brand-maroon shrink-0 mt-0.5" aria-hidden="true" data-id="element-2073" data-mp="2c7yuellvdu5s" />
            <p className="text-sm text-brand-charcoal leading-relaxed" data-id="element-2074" data-mp="2l0aoqik3u1r1">
              Door Mats &amp; Welcome Mats is removed from the customer-facing
              structure. The mat products in that branch, along with the safes,
              are approved for deactivation and are excluded from the active
              catalog.
            </p>
          </div>

          <section className="mt-12" aria-labelledby="recommended-tree-heading" data-id="element-2075" data-mp="2zv721a6rtfaz">
            <h2 id="recommended-tree-heading" className="text-2xl font-serif font-bold text-brand-charcoal mb-3" data-id="element-2076" data-mp="33xty5nah5mdz">
              Recommended customer-facing category tree
            </h2>
            <p className="text-brand-muted leading-relaxed max-w-3xl mb-4" data-id="element-2077" data-mp="eafez8u6sihh">
              This is the same structure used by the site navigation, dropdowns,
              footer, and homepage category sections — all rendered from one
              shared source of truth.
            </p>
            <div className="bg-white border border-brand-border rounded-xl p-5 mb-6 max-w-3xl" data-id="element-2078" data-mp="o6903glz1kav">
              <p className="text-sm text-brand-charcoal leading-relaxed" data-id="element-2079" data-mp="j7fo5smc21ct">
                <strong data-id="element-2080" data-mp="3rpv6dqgviy46">Signs &amp; Addresses is intentionally split.</strong>{' '}
                It was a broad navigation grouping rather than a cleaned backend
                category branch, so it is not a top-level category. Its contents
                now live under Street Signs &amp; Traffic Signs, Decorative
                Signage, and Address Plaques &amp; House Numbers (inside Outdoor
                Accessories &amp; Home Accents).
              </p>
            </div>

            <ol className="grid gap-5 md:grid-cols-2" data-id="element-2081" data-mp="33yy65ayfxp8r">
              {PRODUCT_NAV.map((group, index) => <li key={group.id} className="bg-white border border-brand-border rounded-xl p-5" data-id="element-2082" data-mp="1jg5k42vs2mq6" data-mpi={index}>
                  <h3 className="font-serif font-bold text-brand-charcoal mb-1" data-id="element-2083" data-mp="1ahadwqsqhcmv">
                    {index + 1}. {group.fullLabel}
                  </h3>
                  {group.label !== group.fullLabel && <p className="text-xs text-brand-muted mb-2" data-id="element-2084" data-mp="227ycpw6xq5xd">
                      Header label: {group.label}
                    </p>}
                  <ul className="mt-2 space-y-1.5 text-sm text-brand-muted" data-id="element-2085" data-mp="274x5aaaya4tg">
                    {group.items.map(item => <li key={`${group.id}-${item.label}`} className="pl-4 border-l-2 border-brand-gold/50" data-id="element-2086" data-mp="1y63cyw4nk0lp">
                        {item.label}
                        {item.children && <ul className="mt-1 space-y-1 text-[13px] text-brand-muted/85" data-id="element-2087" data-mp="mpd22zpaaklr">
                            {item.children.map(child => <li key={`${group.id}-${item.label}-${child.label}`} className="pl-4 border-l border-brand-border" data-id="element-2088" data-mp="2jik29jrvn6cm">
                                {child.label}
                                {child.children && <ul className="mt-1 space-y-1" data-id="element-2089" data-mp="25royun05dhgf">
                                    {child.children.map(grandChild => <li key={`${child.label}-${grandChild.label}`} className="pl-4 border-l border-brand-border" data-id="element-2090" data-mp="1vrodywmmqta4">
                                        {grandChild.label}
                                      </li>)}
                                  </ul>}
                              </li>)}
                          </ul>}
                      </li>)}
                  </ul>
                </li>)}
            </ol>
          </section>

          <section className="mt-10" aria-labelledby="audit-heading" data-id="element-2091" data-mp="1lo9cohpdkgss">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4" data-id="element-2092" data-mp="oxqojd71r5zc">
              <h2 id="audit-heading" className="text-2xl font-serif font-bold text-brand-charcoal" data-id="element-2093" data-mp="p03diab82glw">
                Root-only cleanup decisions
              </h2>
              <p className="text-sm text-brand-muted" data-id="element-2094" data-mp="12xi3ph9ausex">
                {assigned.length} approved category assignments ·{' '}
                {deactivated.length} approved to deactivate
              </p>
            </div>

            <div className="bg-white border border-brand-border rounded-xl overflow-hidden" data-id="element-2095" data-mp="2ebvr6l0555uf">
              <div className="overflow-x-auto" data-id="element-2096" data-mp="1pzlethgk4xvb">
                <table className="w-full min-w-[720px] text-left text-sm" data-id="element-2097" data-mp="1q0cxjrp23146">
                  <thead className="bg-brand-background text-brand-charcoal" data-id="element-2098" data-mp="1l2bev7vjl3t8">
                    <tr data-id="element-2099" data-mp="1yson7bccikj3">
                      <th scope="col" className="px-5 py-3 font-bold" data-id="element-2100" data-mp="1ytzqzb5sjsa7">
                        SKU
                      </th>
                      <th scope="col" className="px-5 py-3 font-bold" data-id="element-2101" data-mp="1tzlsgzvu151b">
                        Product
                      </th>
                      <th scope="col" className="px-5 py-3 font-bold" data-id="element-2102" data-mp="23rrr2sglakph">
                        Product status
                      </th>
                      <th scope="col" className="px-5 py-3 font-bold" data-id="element-2103" data-mp="2hpzof1hjfo16">
                        Cleanup status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border/70" data-id="element-2104" data-mp="neb40jsjet9e">
                    {UNASSIGNED_PRODUCTS.map(product => <tr key={product.sku} className="align-top" data-id="element-2105" data-mp="jdhejlzg56ee">
                        <td className="px-5 py-4 font-mono text-xs text-brand-charcoal whitespace-nowrap" data-id="element-2106" data-mp="98so57dbgw2k">
                          {product.sku}
                        </td>
                        <td className="px-5 py-4 text-brand-charcoal font-medium" data-id="element-2107" data-mp="3vnu66lra9rqz">
                          {product.title}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap" data-id="element-2108" data-mp="4f6hbvtu3meo">
                          <span className={`inline-flex items-center text-xs font-bold uppercase tracking-wide px-2 py-1 rounded-sm ${product.status === 'Inactive' ? 'bg-brand-charcoal text-white' : 'bg-brand-green/10 text-brand-green border border-brand-green/30'}`} data-id="element-2109" data-mp="3lo3eeru3xqul">
                            {product.status}
                          </span>
                        </td>
                        <td className="px-5 py-4" data-id="element-2110" data-mp="ix1defvoqdmy">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-2 py-1 rounded-sm mb-2 ${product.action === 'Approved to deactivate' ? 'bg-brand-charcoal/5 text-brand-charcoal border border-brand-border' : 'bg-brand-maroon/10 text-brand-maroon border border-brand-maroon/25'}`} data-id="element-2111" data-mp="iw9h12u9kbvm">
                            {product.action === 'Approved assignment' ? <ArrowRight size={12} aria-hidden="true" data-id="element-2112" data-mp="sm2pvw30ucw6" /> : <EyeOff size={12} aria-hidden="true" data-id="element-2113" data-mp="xkmhac0dwgvx" />}
                            {product.action === 'Approved assignment' ? 'Approved category assignment' : 'Approved to deactivate'}
                          </span>
                          <div className="text-brand-charcoal font-medium" data-id="element-2114" data-mp="39x8qg2i1zajo">
                            {product.suggestion}
                          </div>
                          <p className="text-brand-muted mt-1 leading-relaxed" data-id="element-2115" data-mp="3jtlg8pah9495">
                            {product.note}
                          </p>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer data-id="element-2116" data-mp="2m3qn6unl5fox" />
    </div>;
}